<script setup lang="ts">
import { ref } from "vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { useExercises } from "~/features/computer/composables/useExercises";
import { chapterHubByUnit } from "~/features/computer/data/chapter-hubs";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { computerMcqChapterByUnit } from "~/features/computer/data/mcq-registry";
import type { ComputerMcqUnit } from "~/features/computer/types/computer";

const props = defineProps<{
  unit: Exclude<ComputerMcqUnit, "python">;
}>();

const exercises = useExercises(props.unit);
const config = chapterHubByUnit[props.unit];
const chapter = computerMcqChapterByUnit[props.unit];
const openQuestion = ref<string | null>(null);
const faqs = [
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke long questions kahan milte hain?`,
    answer: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke long questions isi page par structured answers ke sath diye gaye hain.`,
  },
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke long questions kaise yaad karein?`,
    answer: `Jawab ratne ke bajaye steps aur core points samjhein. Is page par isi liye step-by-step answer structure diya gaya hai.`,
  },
  {
    question: `Kya ${chapter.shortTitle} chapter se long questions aate hain?`,
    answer: `Is chapter ki exam importance ke mutabiq long-question preparation useful hai. Chapter hub aur pairing scheme ke sath is page ko use karna best rahega.`,
  },
];
const relatedItems = [
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle}`, path: chapter.routes.chapterTo },
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle} MCQs`, path: chapter.routes.mcqTo },
  { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
  { name: "Guess Paper", path: "/11th/computer/guess-paper/" },
];

function toggleQuestion(id: string) {
  openQuestion.value = openQuestion.value === id ? null : id;
}

usePageSeo({
  title: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Long Questions | Ustaad`,
  description: `Chapter ${config.chapterNumber} ${chapter.shortTitle} long questions structured answers ke sath.`,
  keywords: `chapter ${config.chapterNumber} ${chapter.shortTitle.toLowerCase()} long questions, 11th class computer long questions`,
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: `Ch ${config.chapterNumber} ${chapter.shortTitle}`, item: chapter.routes.chapterTo },
    { name: "Long Questions", item: config.practiceCards[2].to },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Long Questions`,
    items: relatedItems,
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">Chapter {{ config.chapterNumber }} {{ chapter.shortTitle }} Long Questions</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">Most expected long questions ko step by step revise karo. High probability answers yahan structured form me milte hain.</p>
      </section>

      <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <div v-for="question in exercises.long" :key="question.id" class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
          <div class="flex items-center justify-between gap-3">
            <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ question.question }}</p>
            <button type="button" class="rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-800 dark:border-slate-700 dark:text-slate-200" @click="toggleQuestion(question.id)">
              {{ openQuestion === question.id ? "Jawab chhupao" : "Jawab dekho" }}
            </button>
          </div>
          <div v-if="openQuestion === question.id" class="mt-4 space-y-3">
            <ol v-if="question.steps?.length" class="space-y-2 text-sm text-slate-700 dark:text-slate-300">
              <li v-for="(step, index) in question.steps" :key="`${question.id}-${index}`">{{ index + 1 }}. {{ step }}</li>
            </ol>
            <p class="text-sm leading-7 text-slate-700 dark:text-slate-300">{{ question.solution }}</p>
            <pre v-if="question.code" class="overflow-x-auto rounded-2xl bg-slate-950 p-4 text-xs text-slate-100"><code>{{ question.code }}</code></pre>
          </div>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Related Pages</h2>
        <div class="mt-4 grid gap-3 md:grid-cols-2">
          <NuxtLink to="/11th/computer/pairing-scheme/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Pairing Scheme</NuxtLink>
          <NuxtLink to="/11th/computer/important-questions/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Important Questions</NuxtLink>
          <NuxtLink to="/11th/computer/guess-paper/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Guess Paper</NuxtLink>
          <NuxtLink :to="chapter.routes.chapterTo" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Back to Chapter</NuxtLink>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">FAQs</h2>
        <div class="mt-4 space-y-3">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
          >
            <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-700 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
